import React, { useEffect, useRef, useState } from "react";
import { useLaneCopy } from "../../utils/laneCopy";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../../services/api";

// ─── Warm "hanji paper" palette — mirrors SajuResult.tsx for a consistent
// Form → Result visual identity (cream/beige, muted sage, dusty rose, tan).

const KR_FONT = "'Noto Sans KR', sans-serif";
const C = {
  bg: "linear-gradient(160deg, #F6EEE4 0%, #EFE3D3 100%)",
  card: "#FBF6EE",
  cardBorder: "#E6D9C4",
  accent: "#A8A08C",
  accentSoft: "rgba(168,160,140,0.14)",
  accentText: "#6B5A3E",
  textDark: "#3A3226",
  textBody: "#5C5140",
  textMuted: "#9C8F78",
  error: "#B4574A",
};

// Five Elements accent colors — mirrors ELEMENT_META in SajuResult.tsx
const ELEMENT_COLORS = [
  { hanja: "木", color: "#7C9070" }, // wood
  { hanja: "火", color: "#B4574A" }, // fire
  { hanja: "土", color: "#BC9A54" }, // earth
  { hanja: "金", color: "#B8AF98" }, // metal
  { hanja: "水", color: "#5E7A94" }, // water
];

// ─── Date of Birth formatting helpers (mirrors MatescanForm.tsx) ──────────────

const formatDateValue = (value: string): string => {
  const digits = value.replace(/\D/g, "");
  const limitedDigits = digits.slice(0, 8);
  let formatted = "";
  for (let i = 0; i < limitedDigits.length; i++) {
    if (i === 2 || i === 4) formatted += "/";
    formatted += limitedDigits[i];
  }
  return formatted;
};

const validateDateValue = (value: string): string => {
  if (!value) return value;
  const parts = value.split("/");
  if (parts.length === 3) {
    let day = parseInt(parts[0], 10);
    let month = parseInt(parts[1], 10);
    let year = parseInt(parts[2], 10);
    if (isNaN(day)) day = 1;
    if (day < 1) day = 1;
    if (day > 31) day = 31;
    if (isNaN(month)) month = 1;
    if (month < 1) month = 1;
    if (month > 12) month = 12;
    if (isNaN(year)) year = new Date().getFullYear();
    if (year < 1900) year = 1900;
    if (year > 2020) year = 2020;
    return `${day.toString().padStart(2, "0")}/${month.toString().padStart(2, "0")}/${year}`;
  }
  return value;
};

const DateOfBirthField: React.FC<{
  value: string;
  onChange: (v: string) => void;
  t: SajuStrings;
}> = ({ value, onChange, t }) => {
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target;
    const rawValue = input.value;
    const cursorPosition = input.selectionStart || 0;
    const oldFormatted = rawValue;
    const newFormatted = formatDateValue(rawValue);
    onChange(newFormatted);
    setTimeout(() => {
      if (input === document.activeElement) {
        let newCursorPos = cursorPosition;
        if (cursorPosition === 3 && newFormatted.length > oldFormatted.length)
          newCursorPos = 4;
        else if (
          cursorPosition === 6 &&
          newFormatted.length > oldFormatted.length
        )
          newCursorPos = 7;
        input.setSelectionRange(newCursorPos, newCursorPos);
      }
    }, 0);
  };

  return (
    <div>
      <label
        className="block text-[10px] sm:text-[11px] font-semibold tracking-[0.18em] uppercase"
        style={{ color: C.accentText }}
      >
        {t.dobLabel}
        <span className="ml-1" style={{ color: C.accent }}>
          *
        </span>
      </label>
      <input
        type="text"
        inputMode="numeric"
        placeholder={t.dobPlaceholder}
        value={value}
        onChange={handleChange}
        onBlur={(e) => onChange(validateDateValue(e.target.value))}
        className="mt-1.5 w-full rounded-full border px-4 sm:px-5 py-2.5 sm:py-3 text-sm sm:text-[15px] shadow-sm focus:outline-none focus:ring-1"
        style={{
          borderColor: C.cardBorder,
          color: C.textDark,
          background: "#FFFDF8",
        }}
        onFocus={(e) => {
          e.currentTarget.style.borderColor = C.accent;
          e.currentTarget.style.boxShadow = `0 0 0 2px ${C.accentSoft}`;
        }}
      />
    </div>
  );
};

const TimeField: React.FC<{
  value: string;
  onChange: (v: string) => void;
  t: SajuStrings;
}> = ({ value, onChange, t }) => (
  <div>
    <label
      className="block text-[10px] sm:text-[11px] font-semibold tracking-[0.18em] uppercase"
      style={{ color: C.accentText }}
    >
      {t.timeLabel}{" "}
      <span
        style={{ color: C.textMuted, textTransform: "none", letterSpacing: 0 }}
      >
        {t.optional}
      </span>
    </label>
    <input
      type="text"
      placeholder={t.timePlaceholder}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="mt-1.5 w-full rounded-full border px-4 sm:px-5 py-2.5 sm:py-3 text-sm sm:text-[15px] shadow-sm focus:outline-none focus:ring-1"
      style={{
        borderColor: C.cardBorder,
        color: C.textDark,
        background: "#FFFDF8",
      }}
      onFocus={(e) => {
        e.currentTarget.style.borderColor = C.accent;
        e.currentTarget.style.boxShadow = `0 0 0 2px ${C.accentSoft}`;
      }}
    />
    <p className="mt-1.5 text-[11px]" style={{ color: C.textMuted }}>
      {t.timeHelper}
    </p>
  </div>
);

// ─── Localization ───────────────────────────────────────────────────────────
// Follows the user's preferred language via useLaneCopy — the same preference
// the backend (sajuController.js) writes the reading itself in.

interface SajuStrings {
  dobLabel: string;
  dobPlaceholder: string;
  timeLabel: string;
  optional: string;
  timePlaceholder: string;
  timeHelper: string;
  loadingSteps: { label: string; icon: string }[];
  loadingTitle: string;
  loadingSubtitle: string;
  eyebrow: string;
  title: string;
  subtitle: string;
  contextLabel: string;
  contextPlaceholder: string;
  submitButton: string;
  footer: string;
  dobRequiredError: string;
  genericError: string;
  networkError: string;
}

const STRINGS: Record<"en" | "ko", SajuStrings> = {
  en: {
    dobLabel: "Date of Birth",
    dobPlaceholder: "DD / MM / YYYY",
    timeLabel: "Birth Time",
    optional: "(optional)",
    timePlaceholder: "e.g. 09:30 AM",
    timeHelper:
      "Adds your Hour Pillar — the layer of your Saju rarely shown to others.",
    loadingSteps: [
      { label: "Reading your Four Pillars...", icon: "命" },
      { label: "Balancing the Five Elements...", icon: "行" },
      { label: "Finding your Yin-Yang rhythm...", icon: "陰" },
      { label: "Tracing today's Saju flow...", icon: "運" },
      { label: "Writing your reading...", icon: "書" },
    ],
    loadingTitle: "Reading Your Saju",
    loadingSubtitle: "Astria Korea · Saju",
    eyebrow: "Astria Korea",
    title: "Saju Reading",
    subtitle:
      "Your Four Pillars, Five Elements, and Yin-Yang — read quietly, honestly.",
    contextLabel: "What's on your mind?",
    contextPlaceholder: "e.g. I'm thinking about a career change this year...",
    submitButton: "Reveal My Saju",
    footer: "a quiet read on your destiny — not a fortune carved in stone",
    dobRequiredError: "Your date of birth is required.",
    genericError: "Failed to generate your reading. Please try again.",
    networkError: "Something went wrong. Please try again.",
  },
  ko: {
    dobLabel: "생년월일",
    dobPlaceholder: "일 / 월 / 년",
    timeLabel: "태어난 시간",
    optional: "(선택)",
    timePlaceholder: "예: 09:30",
    timeHelper: "시주(時柱)를 더합니다 — 사주 중 잘 드러나지 않는 부분이에요.",
    loadingSteps: [
      { label: "사주팔자를 읽는 중...", icon: "命" },
      { label: "오행의 균형을 맞추는 중...", icon: "行" },
      { label: "음양의 흐름을 찾는 중...", icon: "陰" },
      { label: "오늘의 사주 흐름을 살피는 중...", icon: "運" },
      { label: "풀이를 작성하는 중...", icon: "書" },
    ],
    loadingTitle: "사주를 읽고 있어요",
    loadingSubtitle: "Astria Korea · 사주팔자",
    eyebrow: "Astria Korea",
    title: "사주팔자 Saju Reading",
    subtitle: "당신의 사주팔자, 오행, 음양을 조용하고 솔직하게 풀어드립니다.",
    contextLabel: "지금 어떤 고민이 있으신가요?",
    contextPlaceholder: "예: 올해 이직을 고민하고 있어요...",
    submitButton: "내 사주 확인하기",
    footer: "당신의 운명을 조용히 들여다보는 것일 뿐, 정해진 미래가 아닙니다",
    dobRequiredError: "생년월일을 입력해주세요.",
    genericError: "풀이를 생성하지 못했습니다. 다시 시도해주세요.",
    networkError: "문제가 발생했습니다. 다시 시도해주세요.",
  },
};

const LoadingScreen: React.FC = () => {
  const t = useLaneCopy("sajuform", STRINGS);
  const LOADING_STEPS = t.loadingSteps;
  const [activeIdx, setActiveIdx] = useState(0);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    timerRef.current = setInterval(() => {
      setActiveIdx((prev) => {
        const next = prev + 1;
        if (next >= LOADING_STEPS.length) {
          clearInterval(timerRef.current!);
          return prev;
        }
        return next;
      });
    }, 420);
    return () => clearInterval(timerRef.current!);
  }, []);

  const currentStep = LOADING_STEPS[activeIdx];

  return (
    <div
      className="min-h-screen w-full flex flex-col items-center justify-center px-6 py-16 relative overflow-hidden"
      style={{ background: C.bg, fontFamily: KR_FONT }}
    >
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div
          className="absolute -top-24 -left-24 w-72 h-72 rounded-full blur-3xl"
          style={{
            background: `radial-gradient(circle, ${C.accent} 0%, transparent 70%)`,
            opacity: 0.1,
          }}
        />
        <div
          className="absolute -bottom-24 -right-24 w-80 h-80 rounded-full blur-3xl"
          style={{
            background: `radial-gradient(circle, ${C.accent} 0%, transparent 70%)`,
            opacity: 0.12,
          }}
        />
      </div>

      {/* Five Elements orbit ring — five colored nodes circling the core glyph */}
      <div className="relative mb-10 w-[140px] h-[140px]">
        <motion.div
          className="absolute inset-0"
          animate={{ rotate: 360 }}
          transition={{ duration: 9, repeat: Infinity, ease: "linear" }}
        >
          {ELEMENT_COLORS.map((el, i) => {
            const angle = (360 / ELEMENT_COLORS.length) * i - 90;
            const rad = (angle * Math.PI) / 180;
            const radius = 62;
            const x = 70 + radius * Math.cos(rad);
            const y = 70 + radius * Math.sin(rad);
            return (
              <div
                key={i}
                className="absolute w-8 h-8 rounded-full flex items-center justify-center shadow-sm"
                style={{
                  left: x - 16,
                  top: y - 16,
                  background: el.color,
                }}
              >
                <span
                  className="text-sm"
                  style={{
                    color: "#FBF6EE",
                    fontFamily: KR_FONT,
                    transform: `rotate(${-angle}deg)`,
                  }}
                >
                  {el.hanja}
                </span>
              </div>
            );
          })}
        </motion.div>

        <svg
          width="140"
          height="140"
          viewBox="0 0 140 140"
          className="absolute inset-0"
        >
          <circle
            cx="70"
            cy="70"
            r="62"
            fill="none"
            stroke={C.cardBorder}
            strokeWidth="1"
            strokeDasharray="3 6"
            opacity="0.7"
          />
        </svg>

        <motion.div
          className="absolute inset-0 flex items-center justify-center"
          animate={{ scale: [1, 1.06, 1] }}
          transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
        >
          <div
            className="w-14 h-14 rounded-full flex items-center justify-center border shadow-sm"
            style={{ background: C.card, borderColor: `${C.accent}4D` }}
          >
            <span
              className="text-2xl"
              style={{ color: C.accentText, fontFamily: KR_FONT }}
            >
              命
            </span>
          </div>
        </motion.div>
      </div>

      <motion.h2
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-xl sm:text-2xl font-light tracking-wide mb-1 text-center"
        style={{ color: C.accentText }}
      >
        {t.loadingTitle}
      </motion.h2>
      <p
        className="text-xs tracking-[0.18em] uppercase mb-10 text-center"
        style={{ color: C.textMuted }}
      >
        {t.loadingSubtitle}
      </p>

      <div className="w-full max-w-xs min-h-[52px] flex items-center justify-center">
        <AnimatePresence mode="wait">
          <motion.div
            key={activeIdx}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.25, ease: "easeInOut" }}
            className="flex items-center gap-3 px-5 py-3 rounded-2xl border shadow-sm"
            style={{ background: C.accentSoft, borderColor: `${C.accent}4D` }}
          >
            <div
              className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
              style={{
                background: `linear-gradient(135deg, ${C.accent}, ${C.accentText})`,
              }}
            >
              <span className="text-white text-sm">{currentStep.icon}</span>
            </div>
            <span
              className="text-sm font-medium"
              style={{ color: C.accentText }}
            >
              {currentStep.label}
            </span>
          </motion.div>
        </AnimatePresence>
      </div>

      <div className="flex gap-2 mt-6">
        {LOADING_STEPS.map((_, i) => (
          <div
            key={i}
            className="rounded-full transition-all duration-500"
            style={{
              width: i === activeIdx ? 20 : 6,
              height: 6,
              background: i <= activeIdx ? C.accent : C.cardBorder,
            }}
          />
        ))}
      </div>
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

const SajuForm: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const categoryId = searchParams.get("categoryId") || undefined;

  const [dob, setDob] = useState("");
  const [dobTime, setDobTime] = useState("");
  const [userContext, setUserContext] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const t = useLaneCopy("sajuform", STRINGS);

  const dobToIso = (value: string): string => value; // backend expects DD/MM/YYYY directly

  const handleSubmit = async () => {
    if (!dob || dob.split("/").length !== 3) {
      setError(t.dobRequiredError);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const payload = {
        dob: dobToIso(dob),
        dob_time: dobTime || undefined,
        userContext,
        userId: localStorage.getItem("userId") || undefined,
        subCategoryId: categoryId,
      };
      const response = await api.post(
        "/backend/astria-korea-saju/scan",
        payload,
      );
      if (response.data?.success) {
        sessionStorage.setItem("sajuReading", JSON.stringify(response.data));
        navigate("/saju/result");
      } else {
        setLoading(false);
        const errs: string[] = response.data?.errors;
        setError(
          Array.isArray(errs) && errs.length > 0
            ? errs.join(" · ")
            : response.data?.message || t.genericError,
        );
      }
    } catch (err: any) {
      setLoading(false);
      const errs: string[] = err?.response?.data?.errors;
      setError(
        Array.isArray(errs) && errs.length > 0
          ? errs.join(" · ")
          : err?.response?.data?.message || t.networkError,
      );
    }
  };

  if (loading) return <LoadingScreen />;

  return (
    <div
      className="min-h-screen w-full"
      style={{ background: C.bg, fontFamily: KR_FONT }}
    >
      <div className="h-1 w-full" />

      <div className="min-h-screen w-full px-4 sm:px-6 md:px-10 lg:px-16 py-8 sm:py-12">
        <div className="w-full max-w-xl mx-auto">
          {/* Header */}
          <div className="text-center mb-6 sm:mb-8">
            <div className="inline-flex items-center gap-2 mb-3">
              <span
                className="w-1.5 h-1.5 rounded-full"
                style={{ background: C.accent }}
              />
              <span
                className="text-[10px] tracking-[0.25em] uppercase"
                style={{ color: C.textMuted }}
              >
                {t.eyebrow}
              </span>
              <span
                className="w-1.5 h-1.5 rounded-full"
                style={{ background: C.accent }}
              />
            </div>
            <h1
              className="text-xl sm:text-2xl md:text-3xl font-light tracking-wide"
              style={{ color: C.accentText }}
            >
              {t.title}
            </h1>
            <p
              className="text-xs sm:text-sm mt-2"
              style={{ color: C.textMuted }}
            >
              {t.subtitle}
            </p>
          </div>

          {/* Card */}
          <div
            className="rounded-3xl p-5 sm:p-6 md:p-8 shadow-sm border"
            style={{ background: C.card, borderColor: C.cardBorder }}
          >
            <div className="space-y-6 sm:space-y-8">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <DateOfBirthField value={dob} onChange={setDob} t={t} />
                <TimeField value={dobTime} onChange={setDobTime} t={t} />
              </div>

              <div
                className="border-t pt-6 sm:pt-8"
                style={{ borderColor: C.cardBorder }}
              >
                <label
                  className="block text-[10px] sm:text-[11px] font-semibold tracking-[0.18em] uppercase mb-1.5"
                  style={{ color: C.accentText }}
                >
                  {t.contextLabel}{" "}
                  <span
                    style={{
                      color: C.textMuted,
                      textTransform: "none",
                      letterSpacing: 0,
                    }}
                  >
                    {t.optional}
                  </span>
                </label>
                <textarea
                  value={userContext}
                  onChange={(e) => setUserContext(e.target.value)}
                  placeholder={t.contextPlaceholder}
                  rows={3}
                  className="w-full rounded-2xl border px-4 sm:px-5 py-3 text-sm shadow-sm focus:outline-none focus:ring-1 resize-none"
                  style={{
                    borderColor: C.cardBorder,
                    color: C.textDark,
                    background: "#FFFDF8",
                  }}
                  onFocus={(e) => {
                    e.currentTarget.style.borderColor = C.accent;
                    e.currentTarget.style.boxShadow = `0 0 0 2px ${C.accentSoft}`;
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.borderColor = C.cardBorder;
                    e.currentTarget.style.boxShadow = "none";
                  }}
                />
              </div>
            </div>
          </div>

          {/* Error */}
          <AnimatePresence>
            {error && (
              <motion.p
                initial={{ opacity: 0, y: -4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                className="mt-3 text-xs text-center font-medium"
                style={{ color: C.error }}
              >
                {error}
              </motion.p>
            )}
          </AnimatePresence>

          {/* Submit */}
          <div className="mt-6">
            <button
              onClick={handleSubmit}
              className="w-full rounded-full py-3 px-8 text-sm font-semibold text-white shadow-lg transition-all duration-200 hover:opacity-90"
              style={{
                background: `linear-gradient(to right, ${C.accent}, ${C.accentText})`,
              }}
            >
              {t.submitButton}
            </button>
          </div>

          <div
            className="text-center mt-6 text-[10px] sm:text-[11px] tracking-wide"
            style={{ color: C.textMuted }}
          >
            <span
              className="inline-block border-t w-8 sm:w-12 mx-2 align-middle"
              style={{ borderColor: `${C.accent}40` }}
            />
            {t.footer}
            <span
              className="inline-block border-t w-8 sm:w-12 mx-2 align-middle"
              style={{ borderColor: `${C.accent}40` }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default SajuForm;
